import {
  Code as CodeIcon,
  Abc as AbcIcon,
  ReceiptOutlined as ReceiptOutlinedIcon,
  SvgIconComponent,
  AddLink as AddLinkIcon,
} from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { AxiosError } from 'axios';
import { find, map } from 'lodash';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MutateOptions, useQuery, useQueryClient } from 'react-query';
import { Link, generatePath } from 'react-router-dom';

import { fiatCurrencyApi, paymentTypesApi } from 'api';
import { tradeMethodsApi } from 'api/trade-methods.api';
import {
  CloseFormikDialogData,
  CloseFormikDialogResult,
  CrudPage,
  CrudTableAction,
  CrudTableActionType,
  DataGridColumnDefinition,
  dataGridColumns,
  TradeMethodStatusInfo,
} from 'components';
import { ROUTE_PATH } from 'constants/routes';
import { EnabledStatus, FilterDefinitionType, QueryKey } from 'enums';
import {
  useCurrencies,
  useMutation,
  usePartialQuery,
  useUser,
  useUserContext,
  useUserContextUtils,
} from 'hooks';
import { TranslationNamespace } from 'i18n';
import { FilterDefinition, TradeMethod, TradeMethodDto } from 'types';
import {
  filtersUtils,
  formUtils,
  requisitesUtils,
  validationUtils,
} from 'utils';

import { TradeMethodDetailsDialog } from './TradeMethodDetailsDialog';

type Props = {
  archived?: boolean;
};

type TradeMethodsFilters = {
  id: string;
  fiatCurrencyId: string;
  paymentTypeId: string;
  bankId: string;
  enabled: EnabledStatus;
};

export const TradeMethodsPage: React.FC<Props> = ({ archived }) => {
  const { isAdmin, isTechOperator } = useUser();
  const queryClient = useQueryClient();
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const { getPaymentTypeLabel, getNotArchived } = useUserContextUtils();
  const { banks, paymentTypes } = useUserContext();
  const { getFiatCurrencyCode, fiatCurrenciesOptions } = useCurrencies();

  // TODO: move tkeys
  const { t } = useTranslation(TranslationNamespace.Admin, {
    keyPrefix: 'pages.trade_methods',
  });
  const queryResultPaymentTypes = useQuery(
    QueryKey.PaymentTypes,
    async ({ signal }) => {
      const paymentTypes = await paymentTypesApi.getAll({ signal });
      return map(paymentTypes, (paymentType) => ({
        ...paymentType,
        name: getPaymentTypeLabel({ id: paymentType.id }),
      }));
    },
  );

  const queryResultFiatCurrencies = useQuery(
    QueryKey.FiatCurrencies,
    fiatCurrencyApi.getAll,
  );

  const tradeMethodDetailsDialogData = useMemo(
    () => ({
      banks: getNotArchived(banks),
      paymentTypes: queryResultPaymentTypes?.data || [],
      fiatCurrencies: queryResultFiatCurrencies?.data || [],
    }),
    [
      getNotArchived,
      banks,
      queryResultPaymentTypes?.data,
      queryResultFiatCurrencies?.data,
    ],
  );

  const [createTradeMethodDialogOpen, setCreateTradeMethodDialogOpen] =
    useState(false);

  const paths = useMemo(() => {
    if (isAdmin) {
      return {
        automationSources: ROUTE_PATH.ADMIN.TRADE_METHOD_AUTOMATION_SOURCES,
        parsing: ROUTE_PATH.ADMIN.TRADE_METHOD_PARSINGS,
        filesValidation: ROUTE_PATH.ADMIN.TRADE_METHOD_FILES_VALIDATION,
        deeplinks: ROUTE_PATH.ADMIN.TRADE_METHOD_DEEPLINKS,
      };
    } else if (isTechOperator) {
      return {
        automationSources:
          ROUTE_PATH.TECH_OPERATOR.TRADE_METHOD_AUTOMATION_SOURCES,
        parsing: ROUTE_PATH.TECH_OPERATOR.TRADE_METHOD_PARSINGS,
        filesValidation: ROUTE_PATH.TECH_OPERATOR.TRADE_METHOD_FILES_VALIDATION,
        deeplinks: ROUTE_PATH.TECH_OPERATOR.TRADE_METHOD_DEEPLINKS,
      };
    }
  }, [isAdmin, isTechOperator]);

  const { mutate: updateStatus } = useMutation<
    TradeMethod,
    AxiosError,
    { id: string; enabled: boolean }
  >(tradeMethodsApi.updateStatus, {
    notifierType: 'execute',
  });

  const handleStatusChange = useCallback(
    (
      id: string,
      enabled: boolean,
      options: MutateOptions<any, any, any, any>,
    ) => updateStatus({ id, enabled }, options),
    [updateStatus],
  );

  const columns: DataGridColumnDefinition<TradeMethod>[] = useMemo(
    () => [
      {
        header: t('fields.status'),
        valueGetter: (item) => (
          <TradeMethodStatusInfo
            tradeMethod={item}
            updateStatus={handleStatusChange}
          />
        ),
      },
      dataGridColumns.getIdColumn(),
      {
        header: t('fields.fiat_currency'),
        valueKey: 'fiatCurrencyId',
        valueFormatter: (fiatCurrencyId) =>
          find(queryResultFiatCurrencies?.data, { id: fiatCurrencyId })?.code,
      },
      {
        header: t('fields.payment_type'),
        valueKey: 'paymentTypeId',
        valueFormatter: (paymentTypeId) =>
          find(queryResultPaymentTypes?.data, { id: paymentTypeId })?.name,
      },
      {
        header: t('fields.bank'),
        valueKey: 'bankId',
        valueFormatter: (bankId) => find(banks, { id: bankId })?.name,
      },
    ],
    [
      t,
      banks,
      queryResultFiatCurrencies?.data,
      queryResultPaymentTypes?.data,
      handleStatusChange,
    ],
  );
  const queryResult = usePartialQuery(
    archived ? QueryKey.TradeMethodsArchive : QueryKey.TradeMethodsNotArchive,
    archived
      ? tradeMethodsApi.getAllArchivePaginated
      : tradeMethodsApi.getAllNotArchivePaginated,
  );

  const invalidateTradeMethodsQueries = useCallback(() => {
    queryClient.invalidateQueries(QueryKey.TradeMethodsArchive);
    queryClient.invalidateQueries(QueryKey.TradeMethodsNotArchive);
    queryClient.invalidateQueries(QueryKey.TradeMethods);
  }, [queryClient]);

  const { mutate: remove } = useMutation(tradeMethodsApi.remove, {
    onSuccess: () => {
      queryClient.invalidateQueries(QueryKey.TradeMethodsNotArchive);
    },
    notifierType: 'remove',
  });

  const { mutate: restore } = useMutation(tradeMethodsApi.restore, {
    onSuccess: invalidateTradeMethodsQueries,
    notifierType: 'execute',
  });

  const { mutate: update } = useMutation<
    TradeMethod,
    AxiosError,
    { id: string; data: Partial<TradeMethod> }
  >(tradeMethodsApi.update, {
    onSuccess: invalidateTradeMethodsQueries,
  });

  const { mutate: create } = useMutation<
    TradeMethod,
    AxiosError,
    TradeMethodDto
  >(tradeMethodsApi.create);

  const handleUpdate = useCallback(
    (
      item: TradeMethod,
      data: CloseFormikDialogData<Partial<TradeMethod>>,
      closeDialog: () => void,
    ) => {
      update(
        { id: item.id, data: data?.values },
        {
          onSuccess: () => {
            queryClient.invalidateQueries(QueryKey.TradeMethodsNotArchive);
            closeDialog();
          },
          onError: (error: AxiosError) => {
            data?.formikHelpers.setErrors(validationUtils.getFormErrors(error));
          },
        },
      );
    },
    [queryClient, update],
  );

  const handleCreateClick = useCallback(() => {
    setCreateTradeMethodDialogOpen(true);
  }, []);

  const handleCreateTradeMethodDialogClose = useCallback(
    ({ data, ok }: CloseFormikDialogResult<TradeMethodDto>) => {
      if (ok && data) {
        return create(data.values, {
          onSuccess: () => {
            queryClient.invalidateQueries(QueryKey.TradeMethods);
            queryClient.invalidateQueries(QueryKey.TradeMethodsNotArchive);
            data.formikHelpers.resetForm();
            setCreateTradeMethodDialogOpen(false);
          },
          onError: (error: AxiosError) => {
            data.formikHelpers.setErrors(validationUtils.getFormErrors(error));
          },
        });
      }
      setCreateTradeMethodDialogOpen(false);
    },
    [create, queryClient],
  );

  const renderCustomAction = useCallback(
    (props: { title: string; url: string; Icon: SvgIconComponent }) => ({
      type: CrudTableActionType.Custom,
      render: (item: TradeMethod) => (
        <Link to={generatePath(props.url, { id: item.id })}>
          <IconButton title={props.title} color="primary">
            <props.Icon />
          </IconButton>
        </Link>
      ),
    }),
    [],
  );

  const filtersDefinitions: FilterDefinition<TradeMethodsFilters>[] = useMemo(
    () => [
      {
        label: tCommon('filters.id'),
        name: 'id',
        type: FilterDefinitionType.Text,
        format: 'uuid',
      },
      {
        label: tCommon('filters.fiat_currency'),
        name: 'fiatCurrencyId',
        type: FilterDefinitionType.Select,
        options: fiatCurrenciesOptions,
        getDisplayName: getFiatCurrencyCode,
      },
      {
        label: tCommon('filters.payment_type'),
        name: 'paymentTypeId',
        type: FilterDefinitionType.Select,
        options: requisitesUtils.getPaymentTypesOptions(paymentTypes),
        getDisplayName: (value: string) =>
          requisitesUtils.getPaymentTypeLabel(
            find(paymentTypes, { id: value })!,
          ),
      },
      {
        label: tCommon('filters.bank'),
        name: 'bankId',
        type: FilterDefinitionType.Select,
        options: formUtils.getOptions(banks),
        getDisplayName: (value: string) => find(banks, { id: value })?.name,
      },
      filtersUtils.getEnabledStatusFilter<TradeMethodsFilters>(
        tCommon('filters.status'),
        'enabled',
      ),
    ],
    [banks, fiatCurrenciesOptions, getFiatCurrencyCode, paymentTypes, tCommon],
  );

  const actions = useMemo(
    (): CrudTableAction<TradeMethod>[] => [
      renderCustomAction({
        title: t('buttons.automation_sources'),
        url: paths!.automationSources,
        Icon: AbcIcon,
      }),
      renderCustomAction({
        title: t('buttons.parsing'),
        url: paths!.parsing,
        Icon: CodeIcon,
      }),
      renderCustomAction({
        title: t('buttons.files_validation'),
        url: paths!.filesValidation,
        Icon: ReceiptOutlinedIcon,
      }),
      renderCustomAction({
        title: t('buttons.deeplinks'),
        url: paths!.deeplinks,
        Icon: AddLinkIcon,
      }),
      {
        type: CrudTableActionType.Details,
        renderDialog: (props) => (
          <TradeMethodDetailsDialog
            {...tradeMethodDetailsDialogData}
            {...props}
          />
        ),
        onUpdate: handleUpdate,
      },
      {
        type: CrudTableActionType.Remove,
        onRemove: (item, { close }) => remove(item.id, { onSuccess: close }),
      },
    ],
    [
      handleUpdate,
      paths,
      remove,
      t,
      renderCustomAction,
      tradeMethodDetailsDialogData,
    ],
  );

  const archivedActions = useMemo(
    (): CrudTableAction<TradeMethod>[] => [
      {
        type: CrudTableActionType.Restore,
        onRestore: (item) => {
          restore(item.id);
        },
      },
    ],
    [restore],
  );

  return (
    <Fragment>
      <CrudPage
        header={{
          title: t('title'),
          rightContentButton: { onClick: handleCreateClick },
          hide: archived,
        }}
        filters={{ filtersDefinitions }}
        table={{
          paginated: true,
          ignoreState: { refetchLoading: true },
          queryResult,
          columns,
          actions: archived ? archivedActions : actions,
        }}
      ></CrudPage>
      {!archived && (
        <TradeMethodDetailsDialog
          {...tradeMethodDetailsDialogData}
          open={createTradeMethodDialogOpen}
          onClose={handleCreateTradeMethodDialogClose}
        />
      )}
    </Fragment>
  );
};
